import React, { memo, useMemo } from 'react';

import PropTypes from 'prop-types';

import localization from '../../../../shared/strings';
import { AGGREGATED_FIELD_STATE } from '../../../../store/products/helpers/aggregatedFieldState';
import Description from '../../Description';
import { aggregatedStringFieldPropType } from '../helpers/aggregatedFieldPropTypes';
import { mapAggregatedFieldToInputProps } from '../helpers/mapAggregatedFieldToInputProps';
import { resolveAggregatedFieldTooltip } from '../helpers/resolveAggregatedFieldTooltip';
import { useBindAggregatedField } from '../hooks/useBindAggregatedField';
import styles from './AggregatedDescription.module.scss';

const l10n = localization.PRODUCT_INFO;
const aggregatedL10n = l10n.aggregated;

const AggregatedDescription = ({
  field,
  readOnly,
  readOnlyTooltip,
}) => {
  const fieldLabel = l10n.autoMappedFieldLabels.description;
  const bindings = useBindAggregatedField('descriptionHtml', field, fieldLabel);
  const inputProps = mapAggregatedFieldToInputProps(field);

  const isMultiple = field?.state === AGGREGATED_FIELD_STATE.MULTIPLE;
  const isReadOnly = readOnly || inputProps.readOnly;
  const tooltip = resolveAggregatedFieldTooltip({
    field,
    layerReadOnly: readOnly,
    layerReadOnlyTooltip: readOnlyTooltip,
    canSet: !isReadOnly,
    canClear: false,
  });

  const currentValue = field?.state === AGGREGATED_FIELD_STATE.COMMON
    ? (field.value ?? '')
    : '';

  const additionalConfig = useMemo(() => ({
    readonly: isReadOnly,
    readOnly: isReadOnly,
    placeholder: isMultiple ? aggregatedL10n.multipleValues : '',
  }), [isMultiple, isReadOnly]);

  if (!field || field.state === AGGREGATED_FIELD_STATE.NOT_APPLICABLE) return null;

  return (
    <div className={styles.wrapper}>
      <Description
        title="Description"
        currentValue={currentValue}
        additionalConfig={additionalConfig}
        readOnlyTooltip={readOnly ? readOnlyTooltip : null}
        tooltip={tooltip}
        onChange={isReadOnly ? undefined : bindings.onChange}
      />
    </div>
  );
};

AggregatedDescription.propTypes = {
  field: aggregatedStringFieldPropType,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
};

AggregatedDescription.defaultProps = {
  field: null,
  readOnly: false,
  readOnlyTooltip: null,
};

export default memo(AggregatedDescription);
